// src/pages/AdminAttendanceOverview.tsx
import React, { useEffect, useState } from "react";
import { FaCalendarCheck, FaSpinner } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

interface AttendanceMatrixRowDto {
  userId: number;
  name: string;
  role: string;
  joinTimes: Record<string, string | null>;
}

interface AttendanceMatrixResponse {
  dates: string[];
  rows: AttendanceMatrixRowDto[];
}

interface Batch {
  id: number;
  name: string;
  isActive: boolean;
}

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const AdminAttendanceOverview: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [matrix, setMatrix] = useState<AttendanceMatrixResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(true);

  const API = process.env.REACT_APP_API_BASE_URL;

  // Load active batches on mount
  useEffect(() => {
    const loadBatches = async () => {
      try {
        const res = await fetch(`${API}/api/admin/batches`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Failed to load batches");
        const data = await res.json();
        const activeBatches = (Array.isArray(data) ? data : []).filter(
          (b: Batch) => b.isActive
        );
        setBatches(activeBatches);
        // Auto-select first batch if available
        if (activeBatches.length > 0) {
          setSelectedBatchId(activeBatches[0].id);
        }
      } catch (e) {
        console.error("Failed to load batches", e);
      } finally {
        setLoadingBatches(false);
      }
    };
    loadBatches();
  }, [API]);

  // Load attendance matrix when batch is selected
  useEffect(() => {
    if (!selectedBatchId) {
      setMatrix(null);
      return;
    }

    const loadMatrix = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API}/api/admin/batch/${selectedBatchId}/attendance-matrix`,
          { credentials: "include" }
        );
        if (!res.ok) throw new Error("Failed to load attendance matrix");
        const data: AttendanceMatrixResponse = await res.json();
        setMatrix(data);
      } catch (e) {
        console.error("Failed to load attendance matrix", e);
        setMatrix(null);
      } finally {
        setLoading(false);
      }
    };

    loadMatrix();
  }, [selectedBatchId, API]);

  // Helper to count days the person has attendance
  const getAttendanceCount = (row: AttendanceMatrixRowDto) => {
    if (!matrix) return 0;
    return matrix.dates.reduce((count, date) => {
      const t = row.joinTimes[date];
      return t && t.trim() !== "" ? count + 1 : count;
    }, 0);
  };

  const mentorRows = matrix?.rows.filter((row) => row.role === "MENTOR") || [];
  const studentRows = matrix?.rows.filter((row) => row.role !== "MENTOR") || [];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaCalendarCheck />}
        title="Attendance Overview"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Attendance Overview" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Batch Selection */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <label
            htmlFor="batch-select"
            className="block text-gray-700 font-semibold mb-3"
          >
            <FaCalendarCheck className="inline mr-2 text-[#009fe3]" />
            Select Active Batch
          </label>
          {loadingBatches ? (
            <div className="flex items-center gap-2 text-gray-500">
              <FaSpinner className="animate-spin" />
              Loading batches...
            </div>
          ) : batches.length === 0 ? (
            <div className="text-gray-500">No active batches found.</div>
          ) : (
            <select
              id="batch-select"
              value={selectedBatchId || ""}
              onChange={(e) => setSelectedBatchId(Number(e.target.value))}
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
            >
              {batches.map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Attendance Matrix */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-md p-8 flex items-center justify-center">
            <div className="flex items-center gap-3 text-gray-600">
              <FaSpinner className="animate-spin text-2xl text-[#009fe3]" />
              <span className="text-lg">Loading attendance data...</span>
            </div>
          </div>
        ) : !matrix || matrix.rows.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="text-center text-gray-500">
              {selectedBatchId
                ? "No attendance data found for this batch."
                : "Please select a batch to view attendance."}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#009fe3]">
                Attendance Matrix
              </h2>
              <div className="text-sm text-gray-600">
                <span className="font-semibold">{matrix.rows.length}</span>{" "}
                participants •{" "}
                <span className="font-semibold">{matrix.dates.length}</span>{" "}
                sessions
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300 rounded-lg">
                <thead>
                  <tr className="bg-[#e0f7ff] text-[#009fe3]">
                    <th className="py-3 px-4 text-left font-semibold sticky left-0 bg-[#e0f7ff] z-10">
                      Name
                    </th>
                    {matrix.dates.map((date) => (
                      <th
                        key={date}
                        className="py-3 px-4 text-center font-semibold whitespace-nowrap"
                      >
                        {formatDate(date)}
                      </th>
                    ))}
                    <th className="py-3 px-4 text-center font-semibold bg-[#e0f7ff]">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Mentor Rows */}
                  {mentorRows.map((row) => (
                    <tr
                      key={row.userId}
                      className="bg-[#fff7e0] font-semibold hover:bg-[#fff4d1] transition-colors"
                    >
                      <td className="py-3 px-4 text-[#f7a600] sticky left-0 bg-[#fff7e0] z-10">
                        {row.name}{" "}
                        <span className="text-xs font-normal text-gray-600">
                          (Mentor)
                        </span>
                      </td>
                      {matrix.dates.map((date) => (
                        <td
                          key={date}
                          className="py-3 px-4 text-center whitespace-nowrap"
                        >
                          {row.joinTimes[date]?.slice(0, 5) || (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      ))}
                      <td className="py-3 px-4 text-center font-bold bg-[#fff7e0]">
                        {getAttendanceCount(row)}/{matrix.dates.length}
                      </td>
                    </tr>
                  ))}

                  {/* Student Rows */}
                  {studentRows.map((row, idx) => (
                    <tr
                      key={row.userId}
                      className={`${
                        idx % 2 === 0 ? "bg-white" : "bg-[#f0fbff]"
                      } hover:bg-blue-50 transition-colors`}
                    >
                      <td className="py-3 px-4 font-medium text-gray-800 sticky left-0 bg-inherit z-10">
                        {row.name}
                      </td>
                      {matrix.dates.map((date) => (
                        <td
                          key={date}
                          className="py-3 px-4 text-center whitespace-nowrap"
                        >
                          {row.joinTimes[date]?.slice(0, 5) || (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      ))}
                      <td className="py-3 px-4 text-center font-semibold bg-inherit">
                        {getAttendanceCount(row)}/{matrix.dates.length}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminAttendanceOverview;
